import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, InfoIcon } from 'lucide-react';
import { alerts } from '../data/homepage';
const ease = [0.23, 1, 0.32, 1] as const;
const SLIDE = 28;
const ROTATE_MS = 6000;
export function AlertBar() {
  const [expanded, setExpanded] = useState(false);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const alert = alerts[index];
  const goTo = (next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex(next);
  };
  const step = (offset: number) => {
    setDirection(offset);
    setIndex(current => (current + offset + alerts.length) % alerts.length);
  };

  /** Rotate through the notices, unless the resident is reading this one. */
  useEffect(() => {
    if (paused || alerts.length < 2) return;
    const timer = window.setTimeout(() => {
      setDirection(1);
      setIndex(current => (current + 1) % alerts.length);
    }, ROTATE_MS);
    return () => window.clearTimeout(timer);
  }, [paused, index]);
  const slide = {
    initial: {
      opacity: 0,
      x: direction * SLIDE
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: direction * -SLIDE
    }
  };
  return <div className="w-full bg-ville-lime text-ville-navy" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)} data-id="element-40" data-mp="1xuij2aiup29v">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10" data-id="element-41" data-mp="27t7ubkg8roh5">
        <div className="flex items-center gap-x-3 py-2.5" data-id="element-42" data-mp="2ll4k3huftmad">
          <InfoIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-43" data-mp="2lmpcb0ta4t52" />

          <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} aria-controls="alert-details" className="min-w-0 flex-1 overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-navy sm:cursor-default" data-id="element-44" data-mp="2gmbcg61u0pi4">
            <AnimatePresence mode="wait" initial={false} data-id="element-45" data-mp="2xnxgii991msl">
              <motion.div key={index} {...slide} transition={{
              duration: 0.22,
              ease
            }} aria-live="polite" className="flex min-w-0 flex-wrap items-center gap-x-3" data-id="element-46" data-mp="2xlb86gejb39b">
                <p className="line-clamp-2 text-sm font-extrabold sm:line-clamp-none sm:truncate" data-id="element-47" data-mp="3cc1ytl4e3wau">{alert.title}</p>
                {!expanded && <p className="hidden truncate text-sm text-ville-navy/70 sm:block" data-id="element-48" data-mp="3hbwppoo60lvw">{alert.detail}</p>}
              </motion.div>
            </AnimatePresence>
          </button>

          <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} aria-controls="alert-details" className="hidden shrink-0 items-center gap-1.5 rounded text-sm font-extrabold underline-offset-4 sm:inline-flex transition-colors duration-150 ease-ville hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-navy" data-id="element-49" data-mp="3qbthhz5uq0v6">
            {expanded ? 'Show less' : 'Details'}
            <motion.span animate={{
            rotate: expanded ? 180 : 0
          }} transition={{
            duration: 0.2,
            ease
          }} className="inline-flex" data-id="element-50" data-mp="1tdw16k7ixcfh">
              <ChevronDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-51" data-mp="1jnb2kwruo7wk" />
            </motion.span>
          </button>

          {alerts.length > 1 && <div role="tablist" aria-label="Choose a notice" className="flex shrink-0 items-center gap-0.5 pl-2 sm:border-l sm:border-ville-navy/20 sm:pl-3" data-id="element-52" data-mp="1epsrazqd1gle">
              {alerts.map((item, itemIndex) => <button key={item.title} type="button" role="tab" aria-selected={itemIndex === index} aria-label={item.title} onClick={() => goTo(itemIndex)} className="grid h-5 w-3 place-items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-navy sm:w-4" data-id="element-53" data-mp="27e6sfocqwt16" data-mpi={itemIndex}>
                  <span aria-hidden="true" className={`block rounded-full transition-[width,height,background-color] duration-150 ease-ville ${itemIndex === index ? 'h-1.5 w-1.5 bg-ville-navy' : 'h-1 w-1 bg-ville-navy/35 hover:bg-ville-navy/60'}`} data-id="element-54" data-mp="qz5f429ew4t1" />
                </button>)}
            </div>}
        </div>

        <AnimatePresence initial={false} data-id="element-55" data-mp="2flrnn0lh0am9">
          {expanded && <motion.div id="alert-details" key="details" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          height: {
            duration: 0.28,
            ease
          },
          opacity: {
            duration: 0.18,
            ease
          }
        }} className="overflow-hidden" data-id="element-56" data-mp="20vtvyaoc12sb">
              <div className="overflow-hidden border-t border-ville-navy/20 pb-4 pt-3 text-sm leading-relaxed text-ville-navy/80" data-id="element-57" data-mp="2jls0whwu2frq">
                <AnimatePresence mode="wait" initial={false} data-id="element-58" data-mp="2on7pgjy7z4ev">
                  <motion.div key={index} {...slide} transition={{
                duration: 0.22,
                ease
              }} drag={alerts.length > 1 ? 'x' : false} dragDirectionLock dragConstraints={{
                left: 0,
                right: 0
              }} dragElastic={0.18} onDragStart={() => setPaused(true)} onDragEnd={(_, info) => {
                setPaused(false);
                const swipe = info.offset.x;
                const flick = info.velocity.x;
                if (swipe < -60 || flick < -450) step(1);else if (swipe > 60 || flick > 450) step(-1);
              }} className="touch-pan-y" data-id="element-59" data-mp="185laa8ul808z">
                    {alert.body.map((paragraph, paragraphIndex) => <p key={paragraph} className={paragraphIndex === 0 ? undefined : 'mt-2'} data-id="element-60" data-mp="33ph8dxhpgzeq" data-mpi={paragraphIndex}>
                        {paragraph}
                      </p>)}
                    <p className="mt-2 text-ville-navy/60" data-id="element-61" data-mp="3cp4aymm68b9d">{alert.scope}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}